import React, { useState, useRef, useMemo, useEffect } from 'react'

const sensorStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  height: '100%',
  width: '100%',
  overflow: 'hidden',
  pointerEvents: 'none',
  zIndex: -1,
  maxHeight: 'inherit',
  maxWidth: 'inherit'
}

/**
 * Tracks the size of the element this hook's `resizeListener` is
 * rendered into via `ResizeObserver`.
 * @returns {[JSX.Element, {width: number, height: number}]}
 *   `resizeListener` should be rendered as a child of the element
 *   whose size should be tracked; `sizes` holds its current size.
 */
export default function useResizeAware() {
  const sensorRef = useRef(null)
  const [sizes, setSizes] = useState({ width: null, height: null })

  useEffect(() => {
    const parent = sensorRef.current && sensorRef.current.parentElement
    if (!parent) return

    const updateSizes = () =>
      setSizes({
        width: parent.offsetWidth,
        height: parent.offsetHeight
      })

    updateSizes()
    const observer = new ResizeObserver(updateSizes)
    observer.observe(parent)
    return () => observer.disconnect()
  }, [])

  const resizeListener = useMemo(
    () => <div ref={sensorRef} style={sensorStyle} aria-hidden />,
    []
  )

  return [resizeListener, sizes]
}
